import React, { memo, ReactNode, SyntheticEvent, useMemo } from 'react';

import cls from 'classnames';

import _noop from 'lodash/noop';

import ActivitiesLike from '@ott/activities-like';
import ClockIcon from '@ott/icon-small-clock';
import ImageLoader from '@ott/image-loader';
import Money from '@ott/money';
import declOfNum from '@ott/utility-decl-of-num';

import Labels from './blocks/Labels';
import LinkWrapper from './blocks/LinkWrapper';
import Rating from './blocks/Rating';
import combineDayParts from './helpers/combineDayParts';
import combineMovingTypes from './helpers/combineMovingTypes';
import { getDurationText } from './helpers/getDurationText';
import resolveResizedPhotoLink from './helpers/resolveResizedPhotoLink';
import trackKm from './helpers/trackKm';
import { Activity, CommonProps } from './types';

import styles from './ActivityCard.scss';

const imageClassNames = { imageClass: styles.imageLoader };

type Props = CommonProps & {
  additionalInfoNode?: ReactNode;
};

const ActivityCard = ({
  activity,
  additionalInfoNode,
  className,
  isExternalLink,
  onClick = _noop,
  onLikeUpdateSuccess = _noop,
  openInNewTab = false,
  prepareActivityLink,
  specificLocators,
  shouldShowLike = true,
  trackData,
}: Props) => {
  const {
    activityId,
    categories,
    chargeType,
    coverPhoto = '',
    dayPartAvailability,
    description,
    discount,
    durationMinutes,
    favorite,
    minAmountParts,
    price,
    rating,
    title,
    type,
    withoutDiscount,
  }: Activity = activity;

  const to = useMemo(() => prepareActivityLink(activityId), [activityId]);

  const getPriceType = () => {
    if (chargeType === 'person' && minAmountParts) {
      if (minAmountParts === 1) {
        return 'за человека';
      }

      return `за ${minAmountParts} ${declOfNum(minAmountParts, [
        'человека',
        'человек',
        'человек',
      ])}`;
    }

    return 'за экскурсию';
  };

  const renderDuration = () => {
    if (!durationMinutes) {
      return null;
    }

    return (
      <div className={styles.duration}>
        <ClockIcon className={styles.clockIcon} /> {getDurationText(durationMinutes)}
      </div>
    );
  };

  const renderDayParts = () => {
    if (!dayPartAvailability) {
      return null;
    }

    return <div className={styles.dayParts}>{combineDayParts(dayPartAvailability)}</div>;
  };

  const handleLikeClick = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    trackKm.onLikeClick({
      activity,
      isLikedBeforeClick: Boolean(favorite),
      trackData,
    });
  };

  return (
    <LinkWrapper
      className={cls(styles.ActivityCard, className)}
      isExternalLink={isExternalLink}
      onClick={onClick}
      openInNewTab={openInNewTab}
      specificLocators={specificLocators}
      to={to}
    >
      <div className={styles.image}>
        <ImageLoader
          src={resolveResizedPhotoLink(coverPhoto)}
          childrenClassNames={imageClassNames}
          isCropped={true}
        />

        <div className={styles.imageTop}>
          <Labels type={type} discount={discount} categories={categories} />

          {shouldShowLike && (
            <ActivitiesLike
              activityId={activityId}
              className={styles.like}
              isLiked={favorite}
              onClick={handleLikeClick}
              onUpdateSuccess={onLikeUpdateSuccess}
            />
          )}
        </div>

        <div className={styles.imageBottom}>
          <Rating className={styles.rating} rating={rating} />

          {categories && <div className={styles.movingTypes}>{combineMovingTypes(categories)}</div>}
        </div>
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>
        {description && <div className={styles.description}>{description}</div>}

        <div className={styles.bottom}>
          <div className={styles.row}>
            {dayPartAvailability && renderDuration()}
            <div className={styles.price}>
              {withoutDiscount && (
                <Money className={styles.withoutDiscount} amount={withoutDiscount} />
              )}
              <Money className={styles.price} amount={price} />
            </div>
          </div>

          <div className={cls(styles.row, styles.rowIsLast)}>
            {!dayPartAvailability && renderDuration()}
            {renderDayParts()}

            <span className={styles.activityType}>{getPriceType()}</span>
          </div>
        </div>
        {additionalInfoNode}
      </div>
    </LinkWrapper>
  );
};

export default memo(ActivityCard);
